import { type DriverIdleEngineEventsRequest, type IdleEngineAggregationsRequest, IdleEngineRequester, type VehicleIdleEngineEventsRequest } from '@cobliteam/api-lib';

import type { RawDriverIdleEngine, RawDriverIdleEngineEvent, RawVehicleIdleEngine, RawVehicleIdleEngineEvent } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import { useQuery } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getIdleEngineRequester = () => new IdleEngineRequester({ host: env.apiHost });

const VEHICLE_AGGREGATED_KEY = 'VEHICLE-AGGREGATED-IDLE-ENGINE';
const DRIVER_AGGREGATED_KEY = 'DRIVER-AGGREGATED-IDLE-ENGINE';
const VEHICLE_EVENTS_KEY = 'VEHICLE-EVENTS-IDLE-ENGINE';
const DRIVER_EVENTS_KEY = 'DRIVER-EVENTS-IDLE-ENGINE';

const useIdleEngine = () => {
  const useVehicleAggregatedIdleEngines = ({ startDate, endDate, idleEngineThresholdInMinutes, enabled }: IdleEngineAggregationsRequest & { enabled: boolean }) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<RawVehicleIdleEngine[]>({
      queryKey: [VEHICLE_AGGREGATED_KEY, startDate, endDate, idleEngineThresholdInMinutes],
      queryFn: () => getIdleEngineRequester().getVehicleIdleEngineAggregations(startDate, endDate, idleEngineThresholdInMinutes),
      staleTime: 300000,
      enabled,
    });
  };

  const useDriverAggregatedIdleEngines = ({ startDate, endDate, idleEngineThresholdInMinutes, enabled }: IdleEngineAggregationsRequest & { enabled: boolean }) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<RawDriverIdleEngine[]>({
      queryKey: [DRIVER_AGGREGATED_KEY, startDate, endDate, idleEngineThresholdInMinutes],
      queryFn: () => getIdleEngineRequester().getDriverIdleEngineAggregations(startDate, endDate, idleEngineThresholdInMinutes),
      staleTime: 300000,
      enabled,
    });
  };

  const useVehicleIdleEngineEvents = ({ startDate, endDate, vehicleId, idleEngineThresholdInMinutes }: VehicleIdleEngineEventsRequest, enabled: boolean) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<RawVehicleIdleEngineEvent[]>({
      queryKey: [VEHICLE_EVENTS_KEY, startDate, endDate, vehicleId, idleEngineThresholdInMinutes],
      queryFn: () => getIdleEngineRequester().getVehicleIdleEngineEvents(startDate, endDate, vehicleId, idleEngineThresholdInMinutes),
      staleTime: 300000,
      enabled,
    });
  };

  const useDriverIdleEngineEvents = ({ startDate, endDate, driverId, idleEngineThresholdInMinutes }: DriverIdleEngineEventsRequest, enabled: boolean) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<RawDriverIdleEngineEvent[]>({
      queryKey: [DRIVER_EVENTS_KEY, startDate, endDate, driverId, idleEngineThresholdInMinutes],
      queryFn: () => getIdleEngineRequester().getDriverIdleEngineEvents(startDate, endDate, driverId, idleEngineThresholdInMinutes),
      staleTime: 300000,
      enabled,
    });
  };

  return {
    useVehicleAggregatedIdleEngines,
    useDriverAggregatedIdleEngines,
    useVehicleIdleEngineEvents,
    useDriverIdleEngineEvents,
  };
};

export default useIdleEngine;
